import React, { useState } from 'react';
import { analysisAPI } from '../api/client';
import useUserStore from '../store/userStore';
import { NUMBERS } from '../utils/data';
import { C, css } from '../theme';
import { Card, CardTitle, RCard, QuoteBox, Badge } from '../components/Card';
import AIResponse from '../components/AIResponse';

const RCARD_BORDER = { pos: '#4A9E6A', warn: '#C94A4A', spi: '#8B5BD4', neutral: '#5B7FD4' };

export default function HomeScreen() {
  const { user, subscription } = useUserStore();
  const [selected, setSelected] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const [customNums, setCustomNums] = useState([]);
  const [stateText, setStateText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  function toggleNum(id) { setSelected(p => p.includes(id)?p.filter(n=>n!==id):[...p,id]); }
  function addCustom() { const v=customInput.trim(); if(v&&!customNums.includes(v))setCustomNums(p=>[...p,v]); setCustomInput(''); }
  function removeCustom(v) { setCustomNums(p=>p.filter(n=>n!==v)); }

  async function analyze() {
    const all = [...selected, ...customNums];
    if (!all.length) { setError('اختر أو أضف رقماً واحداً على الأقل'); return; }
    setError(''); setLoading(true); setResult(null);
    try {
      const res = await analysisAPI.analyze({ numbers_seen: all, user_state: stateText });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ في الخادم');
    } finally { setLoading(false); }
  }

  return (
    <div>
      <QuoteBox text="حين يتكرر الرقم، لا يهم إن كنت تراه صدفة... الأهم: لماذا تراه الآن؟" src="جذر الفلك، الجزء الثالث" />

      <Card>
        <CardTitle>١ — الأرقام الشائعة</CardTitle>

        {/* Number grid */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(88px, 1fr))', gap:8, marginBottom:4 }}>
          {NUMBERS.map(n => (
            <button key={n.id} type="button" onClick={() => toggleNum(n.id)}
              style={{ ...css.numBtn, ...(selected.includes(n.id)?css.numBtnSel:{}) }}>
              <span style={{ fontSize:13, fontWeight:700, color:selected.includes(n.id)?'#D4A847':'#D4A847', display:'block' }}>{n.label}</span>
              <span style={{ fontSize:9, color:'#9B94A8', marginTop:2, display:'block', lineHeight:1.3 }}>
                {n.meaning.length>22?n.meaning.substring(0,22)+'...':n.meaning}
              </span>
            </button>
          ))}
        </div>

        {/* Custom number */}
        <span style={{ fontSize:11, color:'#9B94A8', letterSpacing:.5, margin:'18px 0 8px', display:'block' }}>٢ — أضف أي رقم رأيته</span>
        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          <input style={{ flex:1, background:'#13132A', border:'1px solid #1E1E3A', borderRadius:11, padding:'11px 14px', color:'#EDE8D8', fontFamily:'Cairo, sans-serif', fontSize:14, outline:'none', transition:'border-color .2s' }}
            value={customInput} onChange={e=>setCustomInput(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();addCustom();}}}
            placeholder="مثال: 1:33 أو 2:47 أو 123..." maxLength={10}/>
          <button type="button" onClick={addCustom}
            style={{ background:'#1A1A35', border:'1px solid #6B4E1A', borderRadius:11, padding:'11px 16px', color:'#D4A847', fontFamily:'Cairo, sans-serif', fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
            + أضف
          </button>
        </div>

        {customNums.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:6 }}>
            {customNums.map(n => (
              <span key={n} style={{ background:'rgba(212,168,71,.1)', border:'1px solid #D4A847', borderRadius:20, padding:'4px 12px', fontSize:12, color:'#D4A847', display:'flex', alignItems:'center', gap:6, animation:'fadeUp .25s ease' }}>
                {n}
                <button type="button" onClick={()=>removeCustom(n)} style={{ background:'none', border:'none', color:'#6B4E1A', cursor:'pointer', fontSize:13, padding:0, lineHeight:1 }}>✕</button>
              </span>
            ))}
          </div>
        )}

        <span style={{ fontSize:11, color:'#9B94A8', letterSpacing:.5, margin:'18px 0 8px', display:'block' }}>٣ — حالتك اليوم</span>
        <textarea style={css.textarea} value={stateText} onChange={e=>setStateText(e.target.value)}
          placeholder={'اكتب ما تشعر به اليوم، أو ما يشغل تفكيرك...\nكلما كانت تفاصيلك أكثر، كانت القراءة أدق'}/>

        {error && <div style={{ background:'rgba(200,74,74,.1)', border:'1px solid #C94A4A', borderRadius:10, padding:'10px 14px', fontSize:12, color:'#E09090', marginBottom:10 }}>{error}</div>}

        {!result && (
          <button type="button" onClick={analyze}
            style={{ width:'100%', background:'linear-gradient(135deg, #8B6420, #D4A847)', border:'none', borderRadius:14, padding:15, color:'#0D0A00', fontFamily:'Cairo, sans-serif', fontSize:15, fontWeight:700, cursor:'pointer', marginTop:12, transition:'all .25s', letterSpacing:.5, opacity:loading?.5:1 }}
            disabled={loading}>
            ✦ اقرأ رسالة الأرقام
          </button>
        )}
      </Card>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign:'center', padding:32 }}>
          <div style={{ display:'inline-flex', gap:8 }}>
            {[0,200,400].map(delay=>(
              <span key={delay} style={{ width:9, height:9, background:'#D4A847', borderRadius:'50%', display:'inline-block', animation:`bounce 1.2s ease-in-out ${delay}ms infinite` }}/>
            ))}
          </div>
          <p style={{ color:'#9B94A8', fontSize:13, marginTop:12 }}>الأرقام تقرأ حالتك من الكتب الثلاثة...</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div style={{ animation:'fadeUp .4s ease' }}>
          {/* AI response */}
          <div style={{ background:`linear-gradient(135deg, #0D0D1A, #0E0E22)`, borderRadius:14, padding:'18px 20px', marginBottom:12, border:`1px solid #1E1E3A`, borderRight:'3px solid #5B7FD4' }}>
            <div style={{ fontSize:12, color:'#D4A847', fontWeight:600, marginBottom:14, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', paddingBottom:12, borderBottom:'1px solid #1E1E3A' }}>
              <span style={{ fontSize:14 }}>✦</span>
              <Badge blue>تحليل من الكتب الثلاثة</Badge>
              <span>قراءتك الشخصية</span>
              {result.asks_remaining!==undefined && <span style={{ marginRight:'auto' }}><Badge>متبقي: {result.asks_remaining}</Badge></span>}
            </div>
            <AIResponse text={result.response} />
          </div>

          {/* Individual numbers */}
          <div style={{ background:`linear-gradient(135deg, #13132A, #1A1A35)`, border:'1px solid #6B4E1A', borderRadius:14, padding:18, marginBottom:12, textAlign:'center' }}>
            <div style={{ fontFamily:'Amiri,serif', fontSize:16, color:'#D4A847' }}>✦ رسائل الأرقام التفصيلية</div>
          </div>

          {selected.map(id => {
            const n = NUMBERS.find(x=>x.id===id);
            if (!n) return null;
            return (
              <div key={id} style={{ background:'#0D0D1A', borderRadius:14, padding:'16px 18px', marginBottom:12, border:'1px solid #1E1E3A', borderRight:`3px solid ${RCARD_BORDER[n.type]||'#D4A847'}` }}>
                <div style={{ fontSize:12, color:'#D4A847', fontWeight:600, marginBottom:8, display:'flex', alignItems:'center', gap:8 }}>
                  {n.label} <Badge>ج{n.book}</Badge>
                </div>
                <div style={{ fontSize:14, lineHeight:1.75, marginBottom:8 }}>{n.meaning}</div>
                <div style={{ fontSize:12, color:'#9B94A8', lineHeight:1.6, borderTop:'1px solid #1E1E3A', paddingTop:10, marginTop:10 }}>
                  <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                    {n.msgs.map(m=><li key={m} style={{ marginBottom:5, paddingRight:14, position:'relative' }}><span style={{ position:'absolute', right:0, color:'#6B4E1A', fontSize:7, top:4 }}>◆</span>{m}</li>)}
                    {n.warn&&<li style={{ color:'#E09090', marginTop:4, paddingRight:14, position:'relative' }}><span style={{ position:'absolute', right:0, color:'#C94A4A', fontSize:7, top:4 }}>◆</span>⚠ {n.warn}</li>}
                  </ul>
                </div>
              </div>
            );
          })}

          {result.notice && (
            <RCard type="warn">
              <div style={{fontSize:12,color:'#9B94A8'}}>⚡ {result.notice}</div>
            </RCard>
          )}

          <button type="button" onClick={() => { setResult(null); setSelected([]); setCustomNums([]); setStateText(''); }}
            style={{ background:'none', border:'1px solid #C94A4A', borderRadius:10, padding:'9px 18px', color:'#E07070', fontSize:12, cursor:'pointer', display:'block', margin:'14px auto 0', fontFamily:'Cairo,sans-serif' }}>
            سؤال جديد
          </button>
        </div>
      )}
    </div>
  );
}
